import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { ArticleService } from 'app/shared/article.service';
import { UtilisateurService } from 'app/shared/utilisateur.service';
import { catchError, map, of, switchMap } from 'rxjs';
import { ConvertToSlugPipe } from './convert-to-slug.pipe';

export const ArticleResolver = (route: ActivatedRouteSnapshot) => {
  const articleService = inject(ArticleService);
  const utilisateurService = inject(UtilisateurService);
  const router = inject(Router);
  const convertToSlugPipe = new ConvertToSlugPipe();

  // Verify we're in the articles context
  if (!route.url.some(segment => segment.path === 'articles')) {
    return of([null, null, false]);
  }

  const isMalformed = !!route.params['malformedSlug'];
  const id = route.paramMap.get('id') || route.params['id'];
  const nom = route.paramMap.get('nom') || route.params['malformedSlug'];

  if (!id) return of([null, null, false]);

  return articleService.getArticle(id).pipe(
    switchMap((article) => {
      if (!article) return of([null, null, false]);
      
      // Additional check that this is actually an article
      if (article.type !== 'article') {
        return of([null, null, false]);
      }
      
      const expectedSlug = convertToSlugPipe.transform(article.nom);
      const actualSlug = convertToSlugPipe.transform(nom);
      const isSlugValid = expectedSlug === actualSlug;
      
      const isValid = isSlugValid && !isMalformed;

      return utilisateurService.getUtilisateur(article.auteur).pipe(
        map((auteur) => [article, auteur, isValid]),
        catchError(() => of([article, null, isValid])),
      );
    }),
    catchError(() => of([null, null, false]))
  );
};

export const NewsResolver = (route: ActivatedRouteSnapshot) => {
  const articleService = inject(ArticleService);
  const utilisateurService = inject(UtilisateurService);
  const router = inject(Router);
  const convertToSlugPipe = new ConvertToSlugPipe();

  // Verify we're in the news context
  if (!route.url.some(segment => segment.path === 'news')) {
    return of([null, null, false]);
  }

  const isMalformed = !!route.params['malformedSlug'];
  const id = route.paramMap.get('id') || route.params['id'];
  const nom = route.paramMap.get('nom') || route.params['malformedSlug'];

  if (!id) return of([null, null, false]);

  return articleService.getArticle(id).pipe(
    switchMap((article) => {
      if (!article) return of([null, null, false]);
      
      // Additional check that this is actually news
      if (article.type !== 'news') {
        return of([null, null, false]);
      }
      
      const expectedSlug = convertToSlugPipe.transform(article.nom);
      const actualSlug = convertToSlugPipe.transform(nom);
      const isSlugValid = expectedSlug === actualSlug;
      
      const isValid = isSlugValid && !isMalformed;

      return utilisateurService.getUtilisateur(article.auteur).pipe(
        map((auteur) => [article, auteur, isValid]),
        catchError(() => of([article, null, isValid])),
      );
    }),
    catchError(() => of([null, null, false]))
  );
};